import React, { useState } from 'react';
import { Modal, Form, Input, DatePicker, message, Row, Col } from 'antd';
import dayjs from 'dayjs';
import Cookies from 'js-cookie';
import { EstateRole, EstateRoleInvitePayload, EstateRoleInviteResponse } from '../../types';
import { inviteToEstateRole } from '../estatelist/estateService';
import { formatSSN, SSN_RULE } from '../estatelist/ssn';

interface InviteRoleModalProps {
  open: boolean;
  onCancel: () => void;
  /** Fired once the invite is sent, so the caller can re-read the rosters. */
  onSuccess: (response: EstateRoleInviteResponse) => void;
  estateId: string;
  /** Which roster the invite is for. Decides the wording and the endpoint. */
  role: EstateRole;
}

const ROLE_LABELS: Record<EstateRole, string> = {
  executor: 'Executor',
  lawyer: 'Lawyer',
};

/**
 * Invite somebody to act for an estate, as its executor or as its lawyer.
 *
 * One modal for both: the two roles differ in which roster they land on, not in
 * what the estate needs to know about them. Beyond name and email it asks for
 * the date of birth, social security number and county the probate petition
 * wants from whoever signs it, because this invite is the only form either of
 * them ever sees and their profile has nowhere else to come from. All of it is
 * optional, so an invite can still go out on an email address alone.
 */
const InviteRoleModal: React.FC<InviteRoleModalProps> = ({
  open,
  onCancel,
  onSuccess,
  estateId,
  role,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState<boolean>(false);
  const label = ROLE_LABELS[role];
  const lower = label.toLowerCase();

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const token = Cookies.get('accessToken');
      if (!token) {
        message.error('Unauthorized. Please log in.');
        setLoading(false);
        onCancel();
        return;
      }

      const payload: EstateRoleInvitePayload = {
        name: values.name,
        email: values.email,
        dob: values.dob ? values.dob.format('YYYY-MM-DD') : undefined,
        ssn: values.ssn || undefined,
        county: values.county || undefined,
      };

      const response = await inviteToEstateRole(estateId, role, payload, token);
      onSuccess(response);
      setLoading(false);
      form.resetFields();
      message.success(response.message);
      onCancel();
    } catch (error: any) {
      if (error?.errorFields) {
        setLoading(false);
        return; // validation, already shown on the fields
      }
      console.error(error);
      if (error.message === 'Unauthorized') {
        message.error('Unauthorized. Please log in.');
      } else {
        message.error(error.message || `Failed to invite ${lower}.`);
      }
      setLoading(false);
    }
  };

  return (
    <Modal
      title={`Add ${label}`}
      open={open}
      onOk={handleOk}
      onCancel={onCancel}
      okText={`Add ${label}`}
      cancelText="Cancel"
      confirmLoading={loading}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label="Name"
          name="name"
          rules={[{ required: true, message: `Please enter the ${lower}'s name!` }]}
        >
          <Input placeholder={`Enter ${lower}'s name`} />
        </Form.Item>
        <Form.Item
          label="Email Address"
          name="email"
          rules={[
            { required: true, message: `Please enter the ${lower}'s email address!` },
            { type: 'email', message: 'Please enter a valid email address!' },
          ]}
        >
          <Input placeholder={`Enter ${lower}'s email`} />
        </Form.Item>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Date of Birth" name="dob">
              <DatePicker
                style={{ width: '100%' }}
                format="YYYY-MM-DD"
                placeholder="Select date of birth"
                disabledDate={(current) => current && current > dayjs().endOf('day')}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="County" name="county">
              <Input placeholder="County of residence" />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          label="Social Security Number"
          name="ssn"
          normalize={formatSSN}
          rules={[SSN_RULE]}
          extra="Optional. Stored encrypted and shown masked (***-**-1234); the full number is only used when generating court documents."
        >
          <Input placeholder="123-45-6789" maxLength={11} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default InviteRoleModal;
